import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getGerenciaData } from "modules/tableros/services/gerencia";
import {
  applyFilters,
  EMPTY_FILTERS,
  filterOptions,
  metrics,
  tableRecords,
  TABLE_LIMIT,
  validateFilters,
} from "../utils/gerenciaModel";

export const useGerenciaData = (filtros, vistaActual, search = "", refreshFlag) => {
  const [snapshot, setSnapshot] = useState({
    tipo: vistaActual,
    records: [],
    loading: true,
    error: "",
    updatedAt: null,
  });
  const [applied, setApplied] = useState({ tipo: vistaActual, values: EMPTY_FILTERS });
  const [validation, setValidation] = useState("");
  const requestId = useRef(0);
  const abort = useRef(null);
  const recargar = useCallback(async () => {
    const id = ++requestId.current;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setSnapshot((old) => ({
      ...(old.tipo === vistaActual ? old : { tipo: vistaActual, records: [], updatedAt: null }),
      loading: true,
      error: "",
    }));
    try {
      // Mantener una base completa permite limpiar y buscar sin quedarse en un subconjunto del backend.
      const response = await getGerenciaData(vistaActual, {}, { signal: controller.signal });
      const records = Array.isArray(response) ? response : response?.data;
      if (
        !Array.isArray(records) ||
        records.some((item) => !item || typeof item !== "object" || Array.isArray(item))
      )
        throw new Error("El servicio devolvió un formato de datos inválido");
      if (id === requestId.current && !controller.signal.aborted)
        setSnapshot({
          tipo: vistaActual,
          records,
          loading: false,
          error: "",
          updatedAt: new Date(),
        });
    } catch (error) {
      if (id === requestId.current && !controller.signal.aborted)
        setSnapshot((old) => ({
          ...old,
          loading: false,
          error: error.message || "Error al cargar datos",
        }));
    }
  }, [vistaActual]);
  useEffect(() => {
    recargar();
    return () => {
      ++requestId.current;
      abort.current?.abort();
    };
  }, [recargar, refreshFlag]);
  const filtrosAplicados = applied.tipo === vistaActual ? applied.values : EMPTY_FILTERS;
  const records = useMemo(
    () => (snapshot.tipo === vistaActual ? snapshot.records : []),
    [snapshot.tipo, snapshot.records, vistaActual]
  );
  const datos = useMemo(
    () => applyFilters(records, filtrosAplicados, vistaActual),
    [records, filtrosAplicados, vistaActual]
  );
  const table = useMemo(() => tableRecords(datos, search), [datos, search]);
  const buscarConFiltros = useCallback(
    (next = filtros) => {
      const error = validateFilters(next);
      setValidation(error);
      if (error) return false;
      setApplied({ tipo: vistaActual, values: { ...next } });
      return true;
    },
    [filtros, vistaActual]
  );
  const limpiarFiltros = useCallback(() => {
    setApplied({ tipo: vistaActual, values: EMPTY_FILTERS });
    setValidation("");
  }, [vistaActual]);
  return {
    datos,
    datosParaTabla: table.rows,
    totalResultadosTabla: table.total,
    metricasResumen: useMemo(() => metrics(datos, vistaActual), [datos, vistaActual]),
    opcionesFiltros: useMemo(() => filterOptions(records), [records]),
    loading: snapshot.tipo !== vistaActual || snapshot.loading,
    error: snapshot.tipo === vistaActual ? snapshot.error : "",
    updatedAt: snapshot.tipo === vistaActual ? snapshot.updatedAt : null,
    validation,
    buscarConFiltros,
    limpiarFiltros,
    filtrosAplicados,
    recargar,
    limiteRegistros: TABLE_LIMIT,
  };
};
